import { Form, Formik } from "formik";
import { useEffect, useRef, useState } from "react";
import { Alert } from "react-bootstrap";
import { LoadingSpinner } from "../../components/common/Loading/LoadingSpinner";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import { useViewport } from "../../contexts";
import {
    EditFormLifecycleActions,
    EditFormSaveCancelButtons,
    EditFormSplitFooter,
    shouldShowEditFormLifecycleActions,
} from "../../components/Forms";
import type { ILocationDetail, LocationUpdatePayload } from "../../customTypings/location/Location";
import { useGoogleMapsConfig } from "../../hooks/useGoogleMapsConfig";
import { locationsService } from "../../services/locations/locations.service";
import {
    createEditFormSubmitHandler,
    DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE,
    optionalNullableString,
    arePayloadsEqual,
    hydratePostalAddressForForm,
    isUnchangedUpdateResponse,
    mapPostalAddressToApi,
    NO_CHANGES_TOAST_MESSAGE,
    showToastError,
    showToastSuccess,
    showToastUnchanged,
} from "../../utils";
import { formatLocationToastLabel } from "../LocationsListPage/locationAdminListActions";
import { LocationEditFormGrid, type LocationFormValues } from "./LocationEditFormGrid";
import { useLocationEditOutletContext } from "./locationEditOutletContext";

const coordinateField = Yup.number()
    .transform((_value, originalValue) => {
        if (originalValue === "" || originalValue === null || originalValue === undefined) {
            return undefined;
        }
        return Number(originalValue);
    })
    .typeError("Please pick a point on the map or search for an address.")
    .required("Please pick a point on the map or search for an address.");

const validationSchema = Yup.object({
    id: Yup.string().optional(),
    name: Yup.string()
        .transform((v) => (typeof v === "string" ? v.trim() : v))
        .required("Name is required.")
        .max(255, "Name must be at most 255 characters."),
    addressLine1: optionalNullableString,
    addressLine2: optionalNullableString,
    addressLine3: optionalNullableString,
    town: optionalNullableString,
    county: optionalNullableString,
    postcode: optionalNullableString,
    country: optionalNullableString,
    latitude: coordinateField,
    longitude: coordinateField,
    isEnabled: Yup.boolean().required(),
});

const emptyForm: LocationFormValues = {
    name: "",
    ...hydratePostalAddressForForm({}),
    latitude: null,
    longitude: null,
    isEnabled: true,
};

const LocationEditFormView = () => {
    const navigate = useNavigate();
    const { width } = useViewport();
    const isNarrowScreen = width < 768;
    const { locationId, locationDetail, metaDeleted, metaEnabled, setConfirmKind } =
        useLocationEditOutletContext();
    const { config: mapConfig, loading: mapConfigLoading, error: mapConfigError } = useGoogleMapsConfig();

    const mapsEnabled =
        mapConfig?.enabled === true && Boolean(mapConfig.browserApiKey) && Boolean(mapConfig.mapId);

    const [initialValues, setInitialValues] = useState<LocationFormValues>(emptyForm);
    const [liveValidationAfterSubmit, setLiveValidationAfterSubmit] = useState(false);
    const savedUpdatePayloadRef = useRef<LocationUpdatePayload | null>(null);

    const buildUpdatePayload = (values: LocationFormValues): LocationUpdatePayload => ({
        id: values.id!,
        name: values.name.trim(),
        latitude: values.latitude!,
        longitude: values.longitude!,
        ...mapPostalAddressToApi(values),
    });

    const buildUpdatePayloadFromDetail = (location: ILocationDetail): LocationUpdatePayload =>
        buildUpdatePayload({
            id: location.id,
            name: location.name,
            ...hydratePostalAddressForForm(location),
            latitude: location.latitude ?? null,
            longitude: location.longitude ?? null,
            isEnabled: location.isEnabled,
        });

    useEffect(() => {
        if (!locationDetail) return;
        setInitialValues({
            id: locationDetail.id,
            name: locationDetail.name,
            ...hydratePostalAddressForForm(locationDetail),
            latitude: locationDetail.latitude ?? null,
            longitude: locationDetail.longitude ?? null,
            isEnabled: locationDetail.isEnabled,
        });
        savedUpdatePayloadRef.current = buildUpdatePayloadFromDetail(locationDetail);
    }, [locationDetail]);

    if (mapConfigLoading || !locationDetail) {
        return (
            <div className="my-4 d-flex justify-content-center">
                <LoadingSpinner text="Loading location…" />
            </div>
        );
    }

    return (
        <>
            {mapConfigError && (
                <Alert variant="danger" className="mb-3">
                    {mapConfigError}
                </Alert>
            )}

            {!mapsEnabled && (
                <Alert variant="warning" className="mb-3">
                    Maps are not set up for this system. You cannot use the map or address search until an administrator enables maps.
                    {" "}You can still edit and save existing coordinates loaded from the record.
                </Alert>
            )}

            <Formik<LocationFormValues>
                enableReinitialize
                initialValues={initialValues}
                validationSchema={validationSchema}
                validateOnMount={false}
                validateOnChange
                validateOnBlur
                onSubmit={async (values) => {
                    if (metaDeleted) return;
                    setLiveValidationAfterSubmit(true);
                    try {
                        if (!values.id) return;
                        const updatePayload = buildUpdatePayload(values);
                        if (arePayloadsEqual(updatePayload, savedUpdatePayloadRef.current)) {
                            setLiveValidationAfterSubmit(false);
                            showToastUnchanged(NO_CHANGES_TOAST_MESSAGE);
                            return;
                        }
                        const result = await locationsService.update(updatePayload);
                        if (isUnchangedUpdateResponse(result)) {
                            setLiveValidationAfterSubmit(false);
                            showToastUnchanged(result.message ?? NO_CHANGES_TOAST_MESSAGE);
                            return;
                        }
                        setLiveValidationAfterSubmit(false);
                        showToastSuccess(`${formatLocationToastLabel(values.name)} updated successfully!`);
                        navigate("/locations");
                    } catch {
                        showToastError(DEFAULT_FORM_SAVE_ERROR_TOAST_MESSAGE);
                    }
                }}
            >
                {({ isSubmitting, values, submitCount, handleSubmit, validateForm, setTouched }) => {
                    const saveDisabled = metaDeleted;

                    return (
                        <Form
                            noValidate
                            onSubmit={createEditFormSubmitHandler(validateForm, setTouched, values, handleSubmit)}
                        >
                            {metaDeleted && (
                                <Alert variant="warning" className="mb-3">
                                    This location is deleted. Restore it before editing its details.
                                </Alert>
                            )}

                            <LocationEditFormGrid
                                mapConfig={mapConfig}
                                mapsEnabled={mapsEnabled}
                                metaDeleted={metaDeleted}
                                isNew={false}
                                liveValidationAfterSubmit={liveValidationAfterSubmit}
                                submitCount={submitCount}
                            />

                            <EditFormSplitFooter
                                start={
                                    shouldShowEditFormLifecycleActions(false, values.id, isNarrowScreen) ? (
                                        <div className="d-flex flex-wrap gap-2">
                                            <EditFormLifecycleActions
                                                metaDeleted={metaDeleted}
                                                metaEnabled={metaEnabled}
                                                onAction={setConfirmKind}
                                            />
                                        </div>
                                    ) : undefined
                                }
                            >
                                <EditFormSaveCancelButtons
                                    isSubmitting={isSubmitting}
                                    disabled={saveDisabled}
                                    saveLabel="Save"
                                    onCancel={() => navigate("/locations")}
                                />
                            </EditFormSplitFooter>
                        </Form>
                    );
                }}
            </Formik>
        </>
    );
};

export { LocationEditFormView };
